import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import { BorderColorOutlined, DeleteOutline, Edit } from '@mui/icons-material';
import {
  capitalizeWords,
  getStatusLabel,
  lockWidth,
} from '../../../utils/helper';
import { ErrorCode } from '../../../utils/entities/interface';
import { SkeletonRows } from '../../skeletons/list';
import SideDrawer from '../../atoms/Drawer';
import { CreateErrorCodeComp } from '../add/errorcode';
import { AddEditErrorcodeModal } from '../add/errorcodeModal';
import { ListTable } from '../../molecules/ListTable';
import { DeleteErrorCode } from '../../../services/brands';
import useSnackbar from '../../../hooks/useSnackbar';

export const ErrorCodeList = ({
  ErrorCodes,
  onUpdate,
  isLoading,
  brand,
  headerCells,
  handleAddModal,
  setErrorData,
}: {
  ErrorCodes: ErrorCode[];
  onUpdate: () => void;
  isLoading: boolean;
  brand: any;
  headerCells: [string, number][];
  handleAddModal: any;
  setErrorData: any;
}) => {
  const { showSnackbar, SnackbarComponent } = useSnackbar();
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [selectedErrorCode, setSelectedErrorCode] = useState<ErrorCode | null>(
    null,
  );
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedToDelete, setSelectedToDelete] = useState<ErrorCode | null>(
    null,
  );

  const handleDeleteClick = (error: ErrorCode) => {
    setSelectedToDelete(error);
    setDeleteOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedErrorCode(null);
  };

  const confirmDelete = async () => {
    if (!selectedToDelete?._id || !brand?.brandId) return;

    try {
      setDeleteLoading(true);
      await DeleteErrorCode({
        brandId: brand.brandId,
        errorCodeId: selectedToDelete._id,
      });
      showSnackbar('Error code deleted successfully', 'success');
      onUpdate();
    } catch (err) {
      console.error('Delete failed', err);
      showSnackbar('Failed to delete error code', 'error');
    } finally {
      setDeleteLoading(false);
      setDeleteOpen(false);
      setSelectedToDelete(null);
    }
  };

  const actions = [
    {
      icon: <BorderColorOutlined />,
      onClick: (errorcode: ErrorCode) => {
        setErrorData(errorcode);
        handleAddModal();
      },
    },
    {
      icon: <DeleteOutline color="error" />,
      onClick: (errorcode: ErrorCode) => {
        handleDeleteClick(errorcode);
      },
    },
  ].filter(Boolean) as any[];

  return (
    <>
      {SnackbarComponent}
      <ListTable
        headerCells={headerCells}
        rows={ErrorCodes}
        isLoading={isLoading}
        actions={actions}
        renderCell={(errorcode: ErrorCode, colIndex: number) => {
          switch (colIndex) {
            case 0:
              return capitalizeWords(errorcode.acType) || 'N/A';
            case 1:
              return (
                capitalizeWords(getStatusLabel(errorcode.category)) || 'N/A'
              );
            case 2:
              return capitalizeWords(errorcode.models) || 'N/A';
            case 3:
              return errorcode.code.toUpperCase() || 'N/A';
            case 4:
              return capitalizeWords(errorcode.description) || 'N/A';
            case 5:
              return Array.isArray(errorcode.solution) &&
                errorcode.solution.length > 0 &&
                errorcode.solution[0] ? (
                errorcode.solution.map((sol, idx) => (
                  <Typography variant="body2" color="textSecondary" key={idx}>
                    {capitalizeWords(sol)}
                  </Typography>
                ))
              ) : (
                <Typography variant="body2" color="textSecondary">
                  {'N/A'}
                </Typography>
              );
            default:
              return null;
          }
        }}
      />

      <AddEditErrorcodeModal
        open={isDrawerOpen}
        brandId={brand.brandId}
        onClose={handleDrawerClose}
        errorcode={selectedErrorCode}
        onSaved={() => {
          handleDrawerClose();
          onUpdate();
        }}
      />

      <DeleteConfirmDialog
        open={deleteOpen}
        title="Delete Error Code"
        description="Are you sure you want to delete error code"
        itemName={selectedToDelete?.code}
        loading={deleteLoading}
        onClose={() => setDeleteOpen(false)}
        onConfirm={confirmDelete}
      />
    </>
  );
};

interface DeleteConfirmDialogProps {
  open: boolean;
  title?: string;
  description?: string;
  itemName?: string;
  loading?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const DeleteConfirmDialog: React.FC<DeleteConfirmDialogProps> = ({
  open,
  title = 'Delete',
  description = 'Are you sure you want to delete',
  itemName,
  loading = false,
  onClose,
  onConfirm,
}) => {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>

      <DialogContent>
        <DialogContentText>
          {description} <strong>{itemName}</strong>?
        </DialogContentText>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          color="error"
          variant="contained"
          disabled={loading}
        >
          {loading ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
